import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  SelectChangeEvent,
  Paper,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  Typography,
} from '@mui/material';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { FieldConfig, SearchFormTypes } from './SearchForm.types';
import { Box } from '@mui/system';

interface SearchFormWrapperProps {
  fields: FieldConfig<any>[];
  initialValues: Record<string, any>;
  onSubmit: (values: Record<string, any>) => void;
  onReset?: (values: Record<string, any>) => void;
  resetValues?: Record<string, any>;
  showResetButton?: boolean;
  resetButtonLabel?: string;
  validationError?: string;
}

const SearchFormWrapper = ({
  fields,
  initialValues,
  onSubmit,
  onReset,
  resetValues,
  showResetButton = false,
  resetButtonLabel = 'Wyczyść filtry',
  validationError,
}: SearchFormWrapperProps) => {
  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setValues((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'file'
            ? ((e.target as HTMLInputElement).files?.[0] ?? null)
            : value,
    }));
  };

  const handleRadioChange = (name: string, value: string) => {
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(values);
  };

  const handleReset = () => {
    const valuesToReset = resetValues ?? initialValues;
    setValues(valuesToReset);
    if (onReset) {
      onReset(valuesToReset);
    }
  };

  return (
    <Box mb={6}>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit} autoComplete="off">
          <Grid container spacing={2}>
            {fields.map((field) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={field.name}>
                {/* Text input */}
                {field.type === SearchFormTypes.input && (
                  <TextField
                    fullWidth
                    label={field.label}
                    name={field.name}
                    value={values[field.name] ?? ''}
                    onChange={handleChange}
                  />
                )}

                {/* Textarea */}
                {field.type === SearchFormTypes.textarea && (
                  <TextField
                    fullWidth
                    label={field.label}
                    name={field.name}
                    value={values[field.name] ?? ''}
                    onChange={handleChange}
                    multiline
                    rows={field.rows ?? 4}
                  />
                )}

                {/* Select */}
                {field.type === SearchFormTypes.select && (
                  <FormControl fullWidth>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      name={field.name}
                      value={values[field.name] ?? ''}
                      onChange={handleChange}
                      label={field.label}
                    >
                      <MenuItem value="">Wszystkie</MenuItem>
                      {field.options?.map((opt) => (
                        <MenuItem key={String(opt.id)} value={String(opt.id)}>
                          {opt.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Autocomplete */}
                {field.type === SearchFormTypes.autocomplete && (
                  <Autocomplete
                    options={field.options ?? []}
                    value={
                      (field.options ?? []).find(
                        (opt) => String(opt.id ?? '') === String(values[field.name] ?? '')
                      ) ?? null
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.name ?? ''}
                    onChange={(_, selectedOption) => {
                      setValues((prev) => ({
                        ...prev,
                        [field.name]:
                          selectedOption?.id !== undefined && selectedOption?.id !== null
                            ? String(selectedOption.id)
                            : '',
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={field.label} fullWidth />
                    )}
                    clearOnEscape
                  />
                )}

                {/* Radio */}
                {field.type === SearchFormTypes.radio && field.options && (
                  <FormControl component="fieldset">
                    <RadioGroup
                      name={field.name}
                      value={values[field.name] ?? ''}
                      onChange={(e) => handleRadioChange(field.name, e.target.value)}
                    >
                      {field.options.map((opt) => (
                        <FormControlLabel
                          key={String(opt.id)}
                          value={String(opt.id)}
                          control={<Radio />}
                          label={opt.name}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}

                {/* Checkbox */}
                {field.type === SearchFormTypes.checkbox && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        name={field.name}
                        checked={!!values[field.name]}
                        onChange={handleChange}
                      />
                    }
                    label={field.label}
                  />
                )}

                {/* Switch */}
                {field.type === SearchFormTypes.switch && (
                  <FormControlLabel
                    control={
                      <Switch
                        name={field.name}
                        checked={!!values[field.name]}
                        onChange={handleSwitchChange}
                      />
                    }
                    label={field.label}
                  />
                )}

                {/* Number */}
                {field.type === SearchFormTypes.number && (
                  <TextField
                    fullWidth
                    type="number"
                    label={field.label}
                    name={field.name}
                    value={values[field.name] ?? ''}
                    onChange={handleChange}
                  />
                )}

                {/* Email */}
                {field.type === SearchFormTypes.email && (
                  <TextField
                    fullWidth
                    type="email"
                    label={field.label}
                    name={field.name}
                    value={values[field.name] ?? ''}
                    onChange={handleChange}
                  />
                )}

                {/* Date */}
                {field.type === SearchFormTypes.date && (
                  <TextField
                    fullWidth
                    type="date"
                    label={field.label}
                    name={field.name}
                    value={values[field.name] ?? ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                )}

                {/* File */}
                {field.type === SearchFormTypes.file && (
                  <Button fullWidth variant="outlined" component="label">
                    {field.label}
                    <input hidden type="file" name={field.name} onChange={handleChange} />
                  </Button>
                )}
              </Grid>
            ))}
            {validationError && (
              <Grid size={{ xs: 12 }}>
                <Typography color="error" variant="body2">
                  {validationError}
                </Typography>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Button variant="contained" color="primary" type="submit">
                🔍 Wyszukaj
              </Button>
              {showResetButton && (
                <Button
                  sx={{ ml: 2 }}
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<FilterAltOffIcon />}
                  type="button"
                  onClick={handleReset}
                >
                  {resetButtonLabel}
                </Button>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default SearchFormWrapper;
